import { useProfileStore } from "@features/profile/profile.store";
import { cn } from "@shared/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/ui/avatar";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Separator } from "@shared/ui/separator";
import { Link, Outlet } from "@tanstack/react-router";
import { BadgeAlert, Settings, Shield, User } from "lucide-react";
import { useIntl } from "react-intl";

export function ProfileLayout() {
	const intl = useIntl();
	const profile = useProfileStore((state) => state.profile);
	const navItems = [
		{
			key: "/profile/general",
			label: intl.formatMessage({
				id: "page.profile.general.title",
				defaultMessage: "General",
			}),
			icon: <User className="size-5" />,
		},
		{
			key: "/profile/security",
			label: intl.formatMessage({
				id: "page.profile.security.title",
				defaultMessage: "Security",
			}),
			icon: <Shield className="size-5" />,
		},
		{
			key: "/profile/settings",
			label: intl.formatMessage({
				id: "page.profile.settings.title",
				defaultMessage: "Settings",
			}),
			icon: <Settings className="size-5" />,
		},
		{
			key: "/profile/about-app",
			label: intl.formatMessage({
				id: "page.profile.about-app.title",
				defaultMessage: "About the application",
			}),
			icon: <BadgeAlert className="size-5" />,
		},
	];

	const initials = `${profile?.first_name[0] ?? ""}${profile?.last_name[0] ?? ""}`.toUpperCase();

	return (
		<div className="min-h-screen bg-background w-full">
			<div className="container mx-auto    px-6 py-12">
				<div className="grid gap-12 lg:grid-cols-[350px_1fr]">
					<aside className="space-y-5">
						<Card>
							<CardHeader className="flex gap-4 items-center justify-center flex-col">
								<Avatar className="h-20 w-20">
									<AvatarImage
										src={profile?.avatar_url ?? "/images/default-avatar.png"}
										alt={initials}
									/>
									<AvatarFallback className="bg-primary text-primary-foreground text-xl font-semibold">
										{initials}
									</AvatarFallback>
								</Avatar>
								<div className="flex items-center gap-2 flex-col">
									<p className="font-semibold">
										{profile?.first_name} {profile?.last_name}
									</p>
									<Badge variant="secondary" className="gap-1">
										<Shield className="h-5 w-5" /> {profile?.role.toLocaleUpperCase()}
									</Badge>
								</div>
								<Separator className="border my-2" />
							</CardHeader>

							<CardContent className="px-3">
								<nav className="flex flex-row gap-2 lg:flex-col">
									{navItems.map((item) => {
										return (
											<Button asChild key={item.key} variant="ghost" className="p-0 border-0">
												<Link to={item.key} className="h-13">
													{({ isActive }) => (
														<div
															className={cn(
																"flex items-center w-full gap-2.5 h-full  mb-0 rounded-lg px-3",
																{
																	"text-black bg-primary     rounded-lg": isActive,
																	"text-white bg-transparent hover:bg-primary hover:text-black":
																		!isActive,
																}
															)}
														>
															<span>{item.icon}</span>
															{item.label}
														</div>
													)}
												</Link>
											</Button>
										);
									})}
								</nav>
							</CardContent>
						</Card>
					</aside>
					<main>
						<Outlet />
					</main>
				</div>
			</div>
		</div>
	);
}
